// src/components/AccessReadinessChecklist.js
import { useState } from "react";
import { ShieldCheck, ChevronDown, ChevronUp, AlertTriangle, Key, ShoppingBag, Server } from "lucide-react";

const STORAGE_KEY = "skillsyncs_access_confirmed";
const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

const CHECKLIST_ITEMS = [
  {
    id: "stripe",
    label: "Stripe Dashboard access (Settings > Webhooks, API Keys)",
    required: true,
  },
  { id: "shopify", label: "Shopify Admin panel (if relevant)" },
  { id: "hosting", label: "Hosting provider control panel" },
  { id: "domain", label: "Domain registrar account" },
  { id: "dns", label: "DNS provider access" },
  {
    id: "email_platform",
    label: "Email platform admin (Google Workspace / Microsoft 365)",
  },
  {
    id: "server",
    label: "Server or deployment environment (Vercel, Railway, Render, etc.)",
  },
];

function readStoredConfirmation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed?.confirmed || !parsed?.timestamp) return false;
    const age = Date.now() - new Date(parsed.timestamp).getTime();
    return age < TTL_MS;
  } catch {
    return false;
  }
}

function AccessReadinessChecklist({ children }) {
  const [confirmed, setConfirmed] = useState(readStoredConfirmation);
  const [checked, setChecked] = useState({});
  const [accordionOpen, setAccordionOpen] = useState(false);

  // TTL still valid — go straight to the wizard
  if (confirmed) return children;

  const stripeChecked = !!checked["stripe"];

  const toggle = (id) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleContinue = () => {
    const items = CHECKLIST_ITEMS.filter((item) => checked[item.id]).map(
      (item) => item.label
    );
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        confirmed: true,
        timestamp: new Date().toISOString(),
        items,
      })
    );
    setConfirmed(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">

        {/* Header — matches SubmitProblem header block exactly */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Access Readiness Check
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            Before submitting your incident, confirm you have live access to the
            systems your specialist will need.
          </p>
        </div>

        {/* Main Card — identical card style to SubmitProblem */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8 animate-fadeIn">

          {/* Amber warning callout */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                A session cannot be completed without live access to your systems.
              </p>
              <p className="text-sm text-amber-800 mt-1">
                No access = session paused = no refund eligibility.
              </p>
            </div>
          </div>

          {/* Section heading */}
          <h3 className="text-lg font-semibold text-slate-900 mb-1">
            Confirm the systems you currently have access to
          </h3>
          <p className="text-sm text-slate-500 mb-5">
            Check every system you can log into right now before the session starts.
          </p>

          {/* Checklist items */}
          <div className="space-y-3 mb-8">
            {CHECKLIST_ITEMS.map((item) => (
              <label
                key={item.id}
                className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition ${
                  checked[item.id]
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-200 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!checked[item.id]}
                  onChange={() => toggle(item.id)}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-slate-300 rounded flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-sm font-medium transition ${
                      checked[item.id] ? "text-blue-700" : "text-slate-700"
                    }`}
                  >
                    {item.label}
                  </span>
                  {item.required && (
                    <span className="ml-2 text-xs font-semibold text-rose-600 uppercase tracking-wide">
                      Required
                    </span>
                  )}
                </div>
                {checked[item.id] && (
                  <span className="text-emerald-500 text-base flex-shrink-0 font-bold">
                    ✓
                  </span>
                )}
              </label>
            ))}
          </div>

          {/* Continue button + hint — matches SubmitProblem primary CTA */}
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              onClick={handleContinue}
              disabled={!stripeChecked}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ShieldCheck className="w-5 h-5" />
              Continue to submit your incident
            </button>
            <p className="text-xs text-slate-500">
              You only need to confirm access to systems relevant to your issue.
            </p>
          </div>

          {/* Accordion — help for missing access */}
          <div className="mt-8 border border-slate-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setAccordionOpen((prev) => !prev)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-blue-600 hover:bg-slate-50 transition text-left"
            >
              <span>I don't have access yet — what should I do?</span>
              {accordionOpen ? (
                <ChevronUp className="w-4 h-4 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              )}
            </button>

            {accordionOpen && (
              <div className="border-t border-slate-200 px-4 py-5 space-y-5 animate-fadeIn">

                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-slate-500" />
                    Finding your Stripe API Keys
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Log in at{" "}
                    <a
                      href="https://dashboard.stripe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      dashboard.stripe.com
                    </a>
                    , then go to{" "}
                    <span className="font-medium text-slate-800">
                      Developers → API keys
                    </span>
                    . If you don't have access, ask the account owner to invite
                    you as an Administrator or Developer via{" "}
                    <span className="font-medium text-slate-800">
                      Settings → Team
                    </span>
                    .
                  </p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5 text-slate-500" />
                    Recovering Shopify Admin Credentials
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Navigate to{" "}
                    <span className="font-medium text-slate-800">
                      your-store.myshopify.com/admin
                    </span>
                    . If you lost your password, use the{" "}
                    <span className="font-medium text-slate-800">
                      Forgot password
                    </span>{" "}
                    link on the login page. For staff access, ask the store
                    owner to add you via{" "}
                    <span className="font-medium text-slate-800">
                      Settings → Users and permissions
                    </span>
                    .
                  </p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
                    <Server className="w-3.5 h-3.5 text-slate-500" />
                    Regaining Hosting Panel Access
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    For{" "}
                    <span className="font-medium text-slate-800">Vercel</span>:
                    log in at vercel.com and confirm the project appears under
                    your team. For{" "}
                    <span className="font-medium text-slate-800">Railway</span>{" "}
                    or{" "}
                    <span className="font-medium text-slate-800">Render</span>:
                    check that you are a member of the correct workspace. For
                    traditional hosts (cPanel, Plesk), use the credentials sent
                    at signup — recover them via your hosting provider's account
                    recovery page or support chat.
                  </p>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Same fadeIn keyframe used by SubmitProblem.js */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
}

export default AccessReadinessChecklist;
